import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useTranslation } from 'react-i18next';

import { useFormContext } from 'react-hook-form';
import { FiFileText, FiPlus } from 'react-icons/fi';

import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  Icon,
  Input,
  Text,
} from '@brand/components';
import changeFileProperties from 'helpers/changeFileProperties';
import {
  IFieldProps,
  IFieldStyles,
  IUploadFileFieldSchema,
} from 'schema/generator/FormSchema';
import { v4 as uuid } from 'uuid';
import validators from 'validators';

import { useErrorMessage } from 'hooks/form/useErrorMessage';
import { useStyles } from 'hooks/form/useStyles';

import ComponentIsVisible from 'components/utils/IsVisible';
import ComponentToast from 'components/utils/Toast';

import UploadFileFieldCropper from './Cropper';
import MultipleImagesLayout from './MultipleImagesLayout';
import { AiOutlineWarning } from 'react-icons/ai';
import isFileSizeValid from 'helpers/checkFilesSize';

interface IUploadFileData {
  uploadRef: React.MutableRefObject<HTMLInputElement>;
}

const UploadFileField = ({
  isDisabled,
  defaultValue,
  field,
  id,
  name,
  setMoreThanTwoFilesUploaded,
}: IFieldProps<IUploadFileFieldSchema>): JSX.Element => {
  const { isRequired, shouldDisplay, styles = {}, value } = field;
  const fieldStyles = useStyles<IFieldStyles>('textField', styles);
  const { t } = useTranslation(['form', 'common', 'intervention']);
  const { setValue, watch, reset } = useFormContext();

  const errorMessage = useErrorMessage(name);
  const [fileTypesErrorMessage, setFileTypesErrorMessage] = useState(false);
  const [filesAmountError, setFilesAmountError] = useState<boolean>(false);
  const [fileSizeError, setFileSizeError] = useState<boolean>(false);

  const values = watch(name);

  const [fileUploadedType, setFileUploadedType] = useState<string[]>([]);
  const [documentsUploaded, setDocumentsUploaded] = useState<File[]>([]);
  const [photosUploaded, setPhotosUploaded] = useState<string[]>([]);

  useEffect(() => {
    // handle Select Input in parent component
    if (documentsUploaded.length > 1 || photosUploaded.length > 1) {
      setMoreThanTwoFilesUploaded?.(true);
    }

    return () => {
      setMoreThanTwoFilesUploaded?.(false);
    };
  }, [documentsUploaded, photosUploaded, setMoreThanTwoFilesUploaded]);

  const handleEventListener = useCallback(
    (data: IUploadFileData) => {
      if (data.uploadRef.current?.files) {
        const files = data.uploadRef.current.files;
        const filesArray = Array.from(files).map(file => {
          const res = changeFileProperties(
            file,
            `${uuid().slice(0, 4)}${file.name.toLowerCase()}`,
          );
          return res;
        });

        // Check if any of the files are bigger than 10MB
        let oversizedFileName = '';
        const hasInvalidSize = filesArray.some(file => {
          if (!isFileSizeValid(file)) {
            oversizedFileName = file.name.substring(0, 10);
            return true;
          }
          return false;
        });

        if (hasInvalidSize) {
          setFileSizeError(true);
          return;
        }

        // -- allow only 1 file type by upload
        const uploadedFileTypes = {
          pdf: false,
          image: false,
        };
        const multipleFileTypesUploaded = filesArray.map(file => {
          if (file.type.toLowerCase().includes('pdf')) {
            uploadedFileTypes.pdf = true;
          }
          if (file.type.toLowerCase().includes('image')) {
            uploadedFileTypes.image = true;
          }
          return (
            uploadedFileTypes.pdf === true && uploadedFileTypes.image === true
          );
        });
        if (
          multipleFileTypesUploaded &&
          uploadedFileTypes.pdf &&
          uploadedFileTypes.image
        ) {
          setFileTypesErrorMessage(true);
          return;
        }
        // allow only up to 4 documents upload at a time
        if (filesArray.length > 4) {
          setFilesAmountError(true);
          return;
        }
        const imagesUrl: string[] = [];
        filesArray.forEach(file => {
          if (file) {
            setFileUploadedType(prevState => prevState.concat(file.type));

            const fileVerified = validators.fileType(file?.type);

            // check if file type is accepted
            if (!fileVerified) {
              ComponentToast({
                title: 'File type incorrect',
                type: 'warning',
              });
              return;
            }
            if (file?.type === 'application/pdf') {
              setDocumentsUploaded(filesArray);
              setValue(name, filesArray);
            } else {
              const reader = new FileReader();
              reader.readAsDataURL(file as File);
              reader.onload = event => {
                imagesUrl.push(event.target?.result as string);
                setPhotosUploaded(imagesUrl);
                setValue(name, filesArray);
              };
            }
          }
        });
      }
    },
    [name, setValue],
  );

  useEffect(() => {
    if (fileTypesErrorMessage) {
      ComponentToast({
        title: t('intervention:documents.fileTypesError'),
        type: 'warning',
      });
      setFileTypesErrorMessage(false);
    }
    if (filesAmountError) {
      ComponentToast({
        title: t('intervention:documents.filesAmountError'),
        type: 'warning',
      });
      setFilesAmountError(false);
    }
    if (fileSizeError) {
      ComponentToast({
        title: t('intervention:documents.filesSizeError'),
        type: 'warning',
      });
      setFileSizeError(false);
    }
    reset();
  }, [fileSizeError, fileTypesErrorMessage, t, filesAmountError]);

  const uploadFileRef = useRef<HTMLInputElement>({} as HTMLInputElement);
  const handleUploadFile = useCallback(
    (data: IUploadFileData) => {
      data.uploadRef.current.click();
      data.uploadRef.current.addEventListener('change', () =>
        handleEventListener(data),
      );
      data.uploadRef.current.removeEventListener('change', () =>
        handleEventListener(data),
      );
    },
    [handleEventListener],
  );
  const handleRemoveFile = useCallback(() => {
    setValue(name, undefined);
    setDocumentsUploaded([]);
    setPhotosUploaded([]);
    setFileUploadedType([]);
  }, [name, setValue]);

  const isVisible = useMemo(() => {
    return shouldDisplay ? shouldDisplay(values) : true;
  }, [values, shouldDisplay]);

  const cropperIsVisible = useMemo(() => {
    const imageFileOptions = ['image/png', 'image/jpg', 'image/jpeg'];
    const isFileType = imageFileOptions.some(option => {
      return fileUploadedType.find(type => option === type);
    });
    const isVisible = photosUploaded.length === 1 && isFileType;
    return isVisible;
  }, [fileUploadedType, photosUploaded.length]);

  const removeSelectedImage = useCallback(
    (fileIndex: number) => {
      photosUploaded.filter((_, index) => index !== fileIndex);
      const filteredFilesUploaded = photosUploaded.filter(
        e => photosUploaded.indexOf(e) !== fileIndex,
      );
      setValue(
        name,
        values.filter((_: any, index: number) => index !== fileIndex),
      );
      setPhotosUploaded(filteredFilesUploaded);
    },
    [name, photosUploaded, setValue, values],
  );

  return (
    <ComponentIsVisible when={isVisible}>
      <FormControl
        isInvalid={!!errorMessage}
        isRequired={isRequired}
        {...fieldStyles.control}
      >
        <Box>
          <ComponentIsVisible
            when={
              !documentsUploaded.length && !photosUploaded.length && !isDisabled
            }
          >
            <Box
              alignItems="center"
              display="flex"
              justifyContent="center"
              my={6}
            >
              <Button
                height="7rem"
                onClick={() =>
                  handleUploadFile({
                    uploadRef: uploadFileRef,
                  })
                }
                padding="0"
                type="button"
                variant="gray"
                width="7rem"
              >
                <Input
                  accept="image/png, image/jpg, image/jpeg, application/pdf"
                  aria-label={name}
                  data-testid={id}
                  defaultValue={defaultValue || ''}
                  multiple
                  ref={uploadFileRef}
                  sx={{
                    display: 'none',
                  }}
                  type="file"
                  value={value}
                  {...fieldStyles.input}
                />
                <FiPlus size={60} />
              </Button>
            </Box>
          </ComponentIsVisible>
          <ComponentIsVisible when={!!isDisabled}>
            <Flex p={4}>
              <Icon
                as={AiOutlineWarning}
                w={6}
                h={6}
                color="primary.base"
                m={2}
              />
              <Text pt={1} size="sm">
                {t('form:validations.invalidInterventionType')}
              </Text>
            </Flex>
          </ComponentIsVisible>
          {/* //file type pdf */}
          <ComponentIsVisible
            when={!!documentsUploaded.length && !photosUploaded.length}
          >
            <Box
              alignItems="center"
              display="flex"
              flexDirection="column"
              flexWrap="wrap"
              justifyContent="center"
              my={6}
            >
              {documentsUploaded.map(document => {
                return (
                  <Box
                    bg="heading"
                    borderRadius={6}
                    key={document.name}
                    mb={6}
                    padding="8px 16px"
                    width="280px"
                  >
                    <Flex columnGap={4}>
                      <FiFileText color="white" size={22} />
                      <Text color="white" size="md">
                        {(document as File)?.name.substring(0, 18)}
                        {document.name.length > 20 && ' (...)'}
                      </Text>
                    </Flex>
                  </Box>
                );
              })}
              <Button
                fontSize="14px"
                onClick={handleRemoveFile}
                padding="8px 16px"
                size="sm"
                type="button"
                variant="gray"
              >
                {t('common:generic.delete')}
              </Button>
            </Box>
          </ComponentIsVisible>
          {/*  Multiple files */}
          <ComponentIsVisible
            when={!!photosUploaded.length && !documentsUploaded.length}
          >
            <Flex align="center" justify="center" m={6} wrap="wrap">
              {photosUploaded.map((file, index) => (
                <ComponentIsVisible key={uuid()} when={!cropperIsVisible}>
                  <MultipleImagesLayout
                    file={file}
                    fileIndex={index}
                    removeSelectedImage={removeSelectedImage}
                  />
                </ComponentIsVisible>
              ))}
            </Flex>
            {/* Single photo */}
            <ComponentIsVisible when={cropperIsVisible}>
              <UploadFileFieldCropper
                handleRemoveImage={handleRemoveFile}
                imageUrl={photosUploaded[0] as string}
                name={name}
                setFieldValue={setValue}
              />
            </ComponentIsVisible>
          </ComponentIsVisible>
        </Box>
        <ComponentIsVisible when={!!errorMessage}>
          <FormErrorMessage
            justifyContent="center"
            textAlign="center"
            {...fieldStyles.errorMessage}
          >
            {t(`validations.${errorMessage as string}`)}
          </FormErrorMessage>
        </ComponentIsVisible>
      </FormControl>
    </ComponentIsVisible>
  );
};

export default UploadFileField;
